<form
  #entry="ngForm"
  (ngSubmit)="prepareReview()"
  class="pc-dialog flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-solid border-[var(--pc-border)] bg-[var(--pc-surface)] font-sans text-sm leading-5 text-[var(--pc-text)] shadow-2xl"
  [attr.data-theme]="data.theme"
>
  <header
    class="flex shrink-0 items-center justify-between gap-4 border-0 border-b border-solid border-[var(--pc-border)] px-4 py-4 sm:px-6"
  >
    <div class="flex min-w-0 items-center gap-3">
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-lg text-sky-500"
        aria-hidden="true"
      >
        <i class="fa-solid" [class.fa-box-open]="!result" [class.fa-check]="!!result"></i>
      </span>
      <div class="min-w-0">
        <p class="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--pc-muted)]">
          Products <span class="px-1 text-sky-500">/</span> Add Product
        </p>
        <h2 id="product-create-title" class="mt-0.5 text-xl font-bold leading-7 tracking-tight">
          {{ dialogTitle }}
        </h2>
      </div>
    </div>
    <button
      type="button"
      class="pc-icon-button"
      aria-label="Close Add Product"
      (click)="close()"
      [disabled]="saving || uncertainSave"
    >
      <i class="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
    </button>
  </header>

  <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
    <div *ngIf="error" class="pc-error" role="alert">{{ error }}</div>
    <p *ngIf="uncertainSave" class="pc-notice">
      The last save has not been confirmed. Retry the same save; its request ID prevents a
      duplicate.
    </p>
    <p *ngIf="loading" role="status" class="pc-hint mb-4">Loading customer profile…</p>

    <section *ngIf="result as saved" class="space-y-4" aria-live="polite">
      <h3 class="text-lg font-semibold tracking-tight">{{ saved.customer_name }}</h3>
      <p>
        {{ saved.product_rows_created }} product record(s) and {{ saved.package_rows_created }} new
        package(s) saved.
      </p>
      <dl class="pc-summary">
        <ng-container *ngIf="saved.product_uuid"
          ><dt>Product UUID</dt>
          <dd class="pc-id">{{ saved.product_uuid }}</dd></ng-container
        >
        <ng-container *ngIf="saved.package_uuid"
          ><dt>Package UUID</dt>
          <dd class="pc-id">{{ saved.package_uuid }}</dd></ng-container
        >
      </dl>
      <p *ngIf="!saved.package_uuid">
        This carton is unpackaged. A package is required before it can have a label.
      </p>
    </section>

    <section *ngIf="review && !result" class="space-y-4">
      <h3 class="text-lg font-semibold tracking-tight">{{ policy?.customer_name || customer }}</h3>
      <p class="pc-notice">{{ outcome }}</p>
      <dl class="pc-summary">
        <dt>Model</dt>
        <dd>{{ review.product_model_number || 'Not supplied' }}</dd>
        <dt>Description</dt>
        <dd>{{ review.description || 'Not supplied' }}</dd>
        <dt>Serial</dt>
        <dd>{{ review.product_serial_number || 'Not supplied' }}</dd>
        <dt>Carton barcode</dt>
        <dd>{{ review.barcode || 'Not supplied' }}</dd>
        <dt>Packaging</dt>
        <dd>{{ modeLabels[review.packaging_mode] }}</dd>
        <ng-container *ngIf="review.package_uuid"
          ><dt>Selected pallet</dt>
          <dd class="pc-id">
            {{ selectedPallet?.package_name || review.package_uuid }}
          </dd></ng-container
        >
        <ng-container *ngIf="review.package_name"
          ><dt>Package name</dt>
          <dd>{{ review.package_name }}</dd></ng-container
        >
        <dt>Dimensions (in)</dt>
        <dd>{{ review.length || '—' }} × {{ review.width || '—' }} × {{ review.height || '—' }}</dd>
        <dt>Weight (lb)</dt>
        <dd>{{ review.weight || 'Not supplied' }}</dd>
        <dt>PO / SO</dt>
        <dd>{{ review.po_number || '—' }} / {{ review.so_number || '—' }}</dd>
      </dl>
      <p class="pc-hint">
        Saving creates real inventory records. Receiving and warehouse placement happen separately.
      </p>
    </section>

    <div *ngIf="!review && !result" class="space-y-5">
      <section class="space-y-2">
        <label class="pc-label"
          >Customer
          <select
            class="pc-input"
            name="customer"
            [(ngModel)]="customer"
            (ngModelChange)="customerChanged()"
            [disabled]="loading"
            required
          >
            <option value="">Choose a customer</option>
            <option *ngFor="let c of customers" [value]="c.customer_contact_uuid">
              {{ c.customer_name }}
            </option>
          </select>
        </label>
        <p class="pc-hint">Only customers with a configured inventory profile are listed.</p>
      </section>

      <ng-container *ngIf="policy as p">
        <div class="pc-notice">
          <i class="fa-solid fa-box-open shrink-0 text-sky-500" aria-hidden="true"></i>
          <span><strong class="font-semibold">This will create:</strong> {{ outcome }}</span>
        </div>
        <section
          class="space-y-3 border-0 border-t border-solid border-[var(--pc-border)] pt-4"
          aria-label="Customer product catalog"
          *ngIf="!isCarton || catalogTotal > 0"
        >
          <h3 class="pc-section-title">Find a model</h3>
          <label class="pc-label"
            >Search this customer's catalog
            <input
              class="pc-input"
              type="search"
              name="catalogQuery"
              [(ngModel)]="catalogQuery"
              (ngModelChange)="catalogChanged()"
              placeholder="Enter at least 3 characters"
              maxlength="150"
            />
          </label>
          <p class="pc-hint" *ngIf="catalogTotal">
            {{ catalogTotal | number }} catalog items ·
            {{ catalogLoading ? 'Searching…' : 'Search by model or description' }}
          </p>
          <p class="pc-error" *ngIf="catalogError">{{ catalogError }}</p>
          <div class="pc-results" *ngIf="catalog.length">
            <button
              type="button"
              *ngFor="let item of catalog"
              class="pc-choice"
              (click)="selectCatalog(item)"
            >
              <strong>{{ item.product_model_number || 'Unnamed model' }}</strong>
              <span>{{ item.product_name || item.product_description }}</span>
              <small *ngIf="item.serialized">Serial required</small>
            </button>
          </div>
          <p class="pc-hint" *ngIf="catalogMore">
            More matches exist. Add more detail to your search.
          </p>
          <p
            class="pc-hint"
            *ngIf="
              !catalogLoading &&
              !catalogError &&
              catalogQuery.trim().length >= 3 &&
              !catalog.length &&
              !selectedCatalog
            "
          >
            No matching models.
          </p>
          <div *ngIf="selectedCatalog" class="pc-selected">
            Selected: <strong>{{ selectedCatalog.product_model_number }}</strong>
            <button
              type="button"
              class="pc-button"
              (click)="manualEntry()"
              *ngIf="p.manual_entry_allowed && !p.catalog_required"
            >
              Use manual entry
            </button>
          </div>
        </section>

        <section class="space-y-3 border-0 border-t border-solid border-[var(--pc-border)] pt-4">
          <h3 class="pc-section-title">{{ isCarton ? 'Carton details' : 'Product details' }}</h3>
          <div class="grid min-w-0 grid-cols-1 items-end gap-4 sm:grid-cols-2">
            <label class="pc-label" *ngIf="isCarton"
              >Carton barcode *
              <input
                class="pc-input"
                name="barcode"
                [(ngModel)]="draft.barcode"
                maxlength="200"
                [required]="needs('barcode')"
                autocomplete="off"
              />
            </label>
            <label class="pc-label"
              >Model {{ needs('product_model_number') ? '*' : '(optional)' }}
              <input
                class="pc-input"
                name="model"
                [(ngModel)]="draft.model"
                maxlength="200"
                [required]="needs('product_model_number')"
                [readonly]="!!selectedCatalog"
              />
            </label>
            <label class="pc-label"
              >Product code (optional)
              <input class="pc-input" name="code" [(ngModel)]="draft.code" maxlength="200" />
            </label>
            <label class="pc-label sm:col-span-2"
              >Description {{ needs('description') ? '*' : '(optional)' }}
              <input
                class="pc-input"
                name="description"
                [(ngModel)]="draft.description"
                maxlength="1000"
                [required]="needs('description')"
              />
            </label>
            <label class="pc-check min-h-9"
              ><input
                type="checkbox"
                class="m-0 h-4 w-4 shrink-0 accent-sky-600"
                name="serialized"
                [(ngModel)]="draft.serialized"
                [disabled]="selectedCatalog?.serialized === true"
              />
              This item is serialized</label
            >
            <label class="pc-label"
              >Serial {{ serialRequired ? '*' : '(optional)' }}
              <input
                class="pc-input"
                name="serial"
                [(ngModel)]="draft.serial"
                maxlength="200"
                [required]="serialRequired"
                autocomplete="off"
              />
            </label>
            <label class="pc-check min-h-9">
              <input type="checkbox" class="m-0 h-4 w-4 shrink-0 accent-sky-600"
              name="available" [(ngModel)]="draft.available">
              This item is available, and if a package is created, it is also available. 
            </label>
          </div>
        </section>

        <section class="space-y-3 border-0 border-t border-solid border-[var(--pc-border)] pt-4">
          <h3 class="pc-section-title">Packaging</h3>
          <label class="pc-label"
            >Package arrangement
            <select
              class="pc-input"
              name="mode"
              [(ngModel)]="draft.mode"
              (ngModelChange)="packagingChanged()"
            >
              <option *ngFor="let mode of p.packaging_modes" [value]="mode">
                {{ modeLabels[mode] }}
              </option>
            </select>
          </label>
          <label
            class="pc-label"
            *ngIf="draft.mode === 'new_pallet' || draft.mode === 'package_only'"
            >{{ draft.mode === 'new_pallet' ? 'Pallet name *' : 'Package name' }}
            <input
              class="pc-input"
              name="packageName"
              [(ngModel)]="draft.packageName"
              maxlength="200"
              [required]="draft.mode === 'new_pallet'"
            />
          </label>
          <label class="pc-label" *ngIf="draft.mode === 'package_only'"
            >Declared quantity *
            <input
              class="pc-input"
              type="number"
              name="quantity"
              [(ngModel)]="draft.quantity"
              min="1"
              max="100000"
              step="1"
              required
            />
          </label>
          <div *ngIf="draft.mode === 'existing_pallet'">
            <label class="pc-label"
              >Find an unassigned pallet
              <input
                class="pc-input"
                type="search"
                name="palletQuery"
                [(ngModel)]="palletQuery"
                (ngModelChange)="palletChanged()"
                placeholder="At least 3 characters: name, package ID or UUID"
                maxlength="150"
              />
            </label>
            <p class="pc-hint">Pallets already linked to a shipment use the receiving workflow.</p>
            <p *ngIf="palletLoading">Searching pallets…</p>
            <p class="pc-error" *ngIf="palletError">{{ palletError }}</p>
            <div class="pc-results" *ngIf="pallets.length">
              <button
                type="button"
                class="pc-choice"
                *ngFor="let pallet of pallets"
                (click)="selectPallet(pallet)"
              >
                <strong>{{ pallet.package_name || pallet.package_id }}</strong>
                <span>{{ pallet.package_id }} · {{ pallet.product_count }} products</span>
                <small>{{ pallet.package_uuid }}</small>
              </button>
            </div>
            <p class="pc-selected" *ngIf="selectedPallet">
              Selected pallet: {{ selectedPallet.package_name || selectedPallet.package_id }}
            </p>
            <p
              *ngIf="
                !palletLoading &&
                palletQuery.length >= 3 &&
                !pallets.length &&
                !selectedPallet &&
                !palletError
              "
              class="pc-hint"
            >
              No eligible pallets found.
            </p>
          </div>
        </section>

        <details
          class="group rounded-xl border border-solid border-[var(--pc-border)] bg-[var(--pc-subtle)] p-4"
        >
          <summary
            class="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold"
          >
            <span
              >Measurements &amp; references
              <span class="ml-1 text-xs font-normal text-[var(--pc-muted)]">Optional</span></span
            >
            <i
              class="fa-solid fa-chevron-down text-xs text-[var(--pc-muted)] transition-transform group-open:rotate-180"
              aria-hidden="true"
            ></i>
          </summary>
          <div class="mt-4 space-y-4">
            <p class="pc-hint">
              Measurements are for this item. Catalog values are converted to inches and pounds.
            </p>
            <div class="grid min-w-0 grid-cols-2 gap-3 sm:grid-cols-4">
              <label class="pc-label"
                >Length (in)<input
                  class="pc-input"
                  type="number"
                  name="length"
                  [(ngModel)]="draft.length"
                  min="0.000001"
                  max="100000"
                  step="any"
              /></label>
              <label class="pc-label"
                >Width (in)<input
                  class="pc-input"
                  type="number"
                  name="width"
                  [(ngModel)]="draft.width"
                  min="0.000001"
                  max="100000"
                  step="any"
              /></label>
              <label class="pc-label"
                >Height (in)<input
                  class="pc-input"
                  type="number"
                  name="height"
                  [(ngModel)]="draft.height"
                  min="0.000001"
                  max="100000"
                  step="any"
              /></label>
              <label class="pc-label"
                >Weight (lb)<input
                  class="pc-input"
                  type="number"
                  name="weight"
                  [(ngModel)]="draft.weight"
                  min="0.000001"
                  max="100000"
                  step="any"
              /></label>
            </div>
            <div class="grid min-w-0 grid-cols-1 items-end gap-4 sm:grid-cols-2">
              <label class="pc-label"
                >PO<input class="pc-input" name="po" [(ngModel)]="draft.po" maxlength="150"
              /></label>
              <label class="pc-label"
                >SO<input class="pc-input" name="so" [(ngModel)]="draft.so" maxlength="150"
              /></label>
            </div>
          </div>
        </details>
      </ng-container>
    </div>
  </div>

  <footer
    class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-0 border-t border-solid border-[var(--pc-border)] bg-[var(--pc-subtle)] px-4 py-3 sm:px-6"
  >
    <p class="flex items-center gap-2 text-xs text-[var(--pc-muted)]">
      <span class="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true"></span>
      {{ result ? 'Inventory saved' : 'Saves to live inventory' }}
    </p>
    <div class="flex flex-wrap items-center justify-end gap-2">
      <ng-container *ngIf="!review && !result">
        <button type="button" class="pc-button" (click)="close()">Cancel</button>
        <button
          type="button"
          class="pc-button pc-primary"
          (click)="prepareReview()"
          [disabled]="!canReview"
        >
          Review before saving <i class="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
        </button>
      </ng-container>
      <ng-container *ngIf="review && !result">
        <button
          type="button"
          class="pc-button"
          (click)="edit()"
          [disabled]="saving || uncertainSave"
        >
          Back to edit
        </button>
        <button
          type="button"
          class="pc-button"
          [class.pc-primary]="!canPrintCreatedPackage || uncertainSave"
          (click)="save()"
          [disabled]="saving"
        >
          {{ saveButtonLabel }}
        </button>
        <button
          *ngIf="canPrintCreatedPackage && !uncertainSave"
          type="button"
          class="pc-button pc-primary"
          (click)="save(true)"
          [disabled]="saving"
        >
          <i class="fa-solid fa-print" aria-hidden="true"></i>
          {{ saving ? 'Saving…' : 'Save & Print Label' }}
        </button>
      </ng-container>
      <ng-container *ngIf="result as saved">
        <button type="button" class="pc-button" (click)="showRecord()">View saved record</button>
        <button type="button" class="pc-button" (click)="another()">Add another</button>
        <button
          *ngIf="saved.label_available"
          type="button"
          class="pc-button pc-primary"
          (click)="showLabel()"
        >
          <i class="fa-solid fa-print" aria-hidden="true"></i>
          Print Label
        </button>
      </ng-container>
    </div>
  </footer>
</form>
